/**
 * The quick-apply profile a new candidate fills in [KAP-7; Figma `04 · Application (profile)`].
 *
 * **Eleven fields, and the count is the decision.** [Decision 0043] closed the set at exactly the fields of
 * integritystaffing.com's quick-apply form and exists to *remove* steps — adding one is a change to an
 * approved decision, not a design tweak. Work history and a skills multi-select were explicitly taken out.
 *
 * ⚠️ **`consent` is deliberately NOT here any more** [decision 0160]. It was a twelfth key on this draft
 * and a checkbox on the screen; ISS ruled the consent a blocking modal that moves OFF the profile screen,
 * so the answer now arrives from `ConsentDialog` and is an argument to the gate, not a field the form
 * holds. Keeping it on the draft would have left a value nothing on the screen could set.
 */
export interface ProfileDraft {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly payRate: string;
  readonly startDate: string;
  /** `''` until answered — the frame draws two buttons, not a pre-selected default. */
  readonly workAuthorized: '' | 'yes' | 'no';
  readonly isAdult: '' | 'yes' | 'no';
}

export const EMPTY_PROFILE: ProfileDraft = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  zip: '',
  payRate: '',
  startDate: '',
  workAuthorized: '',
  isAdult: '',
};

/** Which fields are wrong, keyed the way the screen renders them. Empty means the form may continue. */
export type ProfileErrors = Partial<Record<keyof ProfileDraft, true>>;

/**
 * What the screen PAINTS right now — derived every render, never stored [Operator, 2026-09-14].
 *
 * The defect this closes: a field stayed red after the candidate had corrected it, and the mark only went
 * away on the next Continue. The marks were a snapshot taken at submit time and kept until the next one, so
 * the screen went on asserting something that had stopped being true — and on a form taller than the kiosk
 * screen that reads as "I did what it asked and it still says no". The prototype clears the mark the moment
 * the field is fixed (its `input`/`change` listeners in `ISS Kiosk updated — Happy Path.html`), and the
 * prototype governs the flow [decision 0155].
 *
 * Two inputs, because they answer two different questions and only one of them can be re-asked here:
 *
 * - `marked` — the fields a refused Continue has already TOLD the candidate about. Sticky per field: once
 *   told, that field is live from then on. **Nothing is ever marked before the first Continue**, which is
 *   the opposite defect and the one an earlier round of feedback was about — a field that turns red halfway
 *   through an email tells somebody they are wrong before they have finished being right.
 * - `refused` — the fields the API named on the last save. These are NOT re-evaluated: the api refused a
 *   value this screen's own rules had already passed, so `validateProfile` would clear the mark instantly
 *   and the candidate would never see it. The only honest local signal is that they have since edited the
 *   field, which is why {@link without} is applied on change instead.
 *
 * The result is that a local rule clears when it is SATISFIED — not when a key is pressed. Half an email
 * address keeps its mark, which is the point: the prototype clears on the first keystroke into a non-empty
 * box and so re-marks the same field on the next Continue.
 */
export function visibleErrors(
  draft: ProfileDraft,
  marked: ProfileErrors,
  refused: ProfileErrors,
): ProfileErrors {
  const invalid = validateProfile(draft);
  const shown: ProfileErrors = {};
  for (const field of Object.keys(EMPTY_PROFILE) as (keyof ProfileDraft)[]) {
    if (refused[field] === true || (marked[field] === true && invalid[field] === true)) {
      shown[field] = true;
    }
  }
  return shown;
}

/** The same set without one field, or the same object when it was not in it (so React can skip the render). */
export function without(errors: ProfileErrors, field: keyof ProfileDraft): ProfileErrors {
  if (errors[field] !== true) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

/**
 * An address-shaped email — the prototype's `isValidEmail`, and the ONE copy of it in this app: the profile
 * page and the check-in form both ask it [ISS-147]. Loose on purpose: a rule stricter than the api's
 * `@IsEmail()` would refuse a real person's address, and the api still has the last word.
 */
export function isEmailShaped(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Validates the draft.
 *
 * **Every field is required, and that is the frame's own claim**: each one carries a validation message, so
 * none is optional. The shapes are deliberately loose — a five-digit ZIP and an address-shaped email — because
 * this runs in front of somebody who cannot ask why they were refused, and a stricter rule rejects real people.
 *
 * ⚠️ **It checks that the two eligibility questions are ANSWERED, never what they answer** — and the
 * distinction is [0161]'s. An unanswered question is a form error the candidate fixes in place; a `no` is a
 * complete, valid answer that the gate then refuses on its own screen [{@link blockingReason}]. Marking a
 * `no` red here would tell somebody their honest answer was a typing mistake.
 */
export function validateProfile(draft: ProfileDraft): ProfileErrors {
  const errors: ProfileErrors = {};
  if (draft.firstName.trim() === '') errors.firstName = true;
  if (draft.lastName.trim() === '') errors.lastName = true;
  if (!isEmailShaped(draft.email)) errors.email = true;
  /*
   * TEN DIGITS, because that is what Bullhorn accepts — its own edit form says «Enter only 10 digits, no
   * spaces, no special characters», and the field is not validated on their side either, so anything else
   * is stored and is wrong.
   *
   * Found 2026-09-02: a record written by this kiosk holds `+542818184`. The screen asked only that the box
   * not be empty and the api allowed 40 characters of anything, so a plus sign and nine digits went in and
   * stayed. This is the one rule on this screen that is NOT deliberately loose, and the reason is that a
   * loose value here does not reject a real person — it silently produces a phone number nobody can dial.
   */
  if (!/^[0-9]{10}$/.test(digitsOnly(draft.phone))) errors.phone = true;
  if (draft.city.trim() === '') errors.city = true;
  if (draft.state === '') errors.state = true;
  if (!/^[0-9]{5}$/.test(draft.zip.trim())) errors.zip = true;
  // A rate, not a salary: decision 0043's 2026-08-05 clarification withdrew the "or annual salary" reading,
  // so `45000` is not a valid answer to this field.
  if (!/^[0-9]{1,3}(\.[0-9]{1,2})?$/.test(draft.payRate.trim())) errors.payRate = true;
  /*
   * **Present and well-formed. The date FLOOR is `DateField`'s rule, not this function's** [decision 0180;
   * RK-037, Operator, 2026-10-19].
   *
   * `DateField` carries `min` = today: the calendar greys out everything earlier, and a day TYPED before it
   * leaves the field as masked text, so the shape check below refuses it with this field's own message.
   * An ISO date in the past reaching this function is **accepted**, by it and by the api both — neither
   * this function nor `candidate.dto.ts` compares against today.
   *
   * The reason is a real person, not an edge case: a returning candidate's record already holds an old
   * availability date, this screen reads it back, and they have no reason to touch it. Refusing it turns
   * *"nothing about me changed"* into a dead end on a field they never edited — exactly the failure BR-K7
   * and decision 0046 forbid. An old date still means what Bullhorn's `dateAvailable` means: they can work
   * from then on.
   *
   * ⚠️ **The asymmetry is the decision, not an inconsistency** [0180], and it is the thing a later reader
   * "fixes". An earlier round removed the floor from BOTH layers on the reasoning that *a floor enforced
   * on only one side is worse than no floor, because the screen would pass a value its own api rejects* —
   * true of a floor that VALIDATES, which is why this one does not. Restricting what can be entered while
   * accepting what is already held is the whole point: nothing the field lets through is refused downstream.
   */
  // Present AND a real ISO date. `DateField` emits ISO only once the eight digits form a date that exists
  // on or after its floor, and passes the masked text through otherwise — so `02/31/2026`, a half-typed
  // value and a typed past day are all caught here rather than travelling as something that looks unfilled.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.startDate.trim())) errors.startDate = true;
  if (draft.workAuthorized === '') errors.workAuthorized = true;
  if (draft.isAdult === '') errors.isAdult = true;
  return errors;
}

/**
 * Which of the three refusals is showing, if one is [Figma · `STATES & OVERLAYS` → `Blocked — cannot
 * continue` → `Blocked · consent` / `Blocked · age` / `Blocked · auth`, three viewports each].
 */
export const BLOCKED_REASON = { consent: 'consent', age: 'age', auth: 'auth' } as const;
export type BlockedReason = (typeof BLOCKED_REASON)[keyof typeof BLOCKED_REASON];

/**
 * Where the application gate stands — `undefined` on the form, and one of these once Continue is pressed
 * [decision 0195].
 *
 * A discriminated union rather than two flags, so "the modal is up" and "a refusal is showing" cannot both
 * be true: they are one position in a sequence, not two independent booleans.
 */
export type ApplicationGate =
  { readonly stage: 'consent' } | { readonly stage: 'blocked'; readonly reason: BlockedReason };

/**
 * The gate [decision 0195]. All THREE answers must pass before anything is created in Bullhorn — consent,
 * 18-or-older, authorized to work — and it is evaluated **after** the consent modal, so a refusal leaves no
 * record to orphan. Returns the refusal to show, or `undefined` when the write may proceed.
 *
 * **This is the only place the three are read together, and that is the point.** 0195's own warning: *"the
 * write becomes a single point… a build that scatters the create across branches will pass each refusal test
 * and still leak a record on some fourth path"*. One predicate, one call site.
 *
 * `!== 'yes'` rather than `=== 'no'` on purpose: an unanswered question can only reach here through a
 * validation bug, and the safe reading of "we were never told" is not "proceed".
 *
 * ⚠️ **The ORDER is derived, not ruled.** 0195 evaluates the three together and draws one frame per
 * refusal, so a candidate who fails two sees one screen and nothing says which. Consent comes first because
 * it is the answer they just gave — the modal is still the last thing they touched, and `Blocked · consent`
 * is the only one of the three whose copy speaks to that action (*"go back and choose 'I agree'"*). Age
 * before auth is the order the design file's own section lists them in. Confirm with the Operator; changing
 * it is this array's order and nothing else.
 */
export function blockingReason(draft: ProfileDraft, consented: boolean): BlockedReason | undefined {
  if (!consented) return BLOCKED_REASON.consent;
  if (draft.isAdult !== 'yes') return BLOCKED_REASON.age;
  if (draft.workAuthorized !== 'yes') return BLOCKED_REASON.auth;
  return undefined;
}

/**
 * What a refused save marks on the form — the api's own field names, kept only where this screen draws one.
 *
 * `ssnLast4`, `birthMonth`, `birthDay` and the api's `operationShape` anchor are deliberately NOT here: the
 * candidate typed those at check-in, two screens ago, and cannot correct them from this one. A refusal that
 * names only those paints nothing and is carried by the screen's summary line instead — better a general
 * message than a red border on a field that is not the problem.
 */
export function rejectionErrors(fields: readonly string[]): ProfileErrors {
  const errors: ProfileErrors = {};
  for (const field of fields) {
    if (Object.hasOwn(EMPTY_PROFILE, field)) errors[field as keyof ProfileDraft] = true;
  }
  return errors;
}

/**
 * Everything that is not a digit, removed.
 *
 * Applied on the way IN as well as on the way out: a returning candidate's record may already hold
 * `+542818184`, and pre-filling that into a box the screen then refuses would blame them for a value the
 * kiosk itself wrote.
 */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}
